import React, { useCallback, useLayoutEffect, useMemo, useState } from "react"
import {
  ActivityIndicator,
  FlatList,
  LayoutChangeEvent,
  Platform,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { Image, ImageStyle } from "expo-image"

import { Text } from "src/components/Ignite/Text"
import { avatarImageMap } from "src/context/RiveContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useCurrentRole } from "src/queries/useRole"
import { ThemedStyle } from "src/theme"
import { ContentLibraryItem } from "./adapters"
import { THUMBNAIL_ASPECT_RATIO, ThumbnailCard } from "./ThumbnailCard"

// Cards are aligned with the ContentLibrarySortBar:
//  - left edge matches the "Latest" sort pill / "Content Library" header
//    (paddingHorizontal: 16 inside primaryRow)
//  - right edge matches the search-icon button
//    (paddingRight: 12 on primaryRowWrapper)
const GRID_PADDING_LEFT = 16
const GRID_PADDING_RIGHT = 12
const CARD_GAP = 12
const ROW_GAP_MULTI = CARD_GAP + 30

// Responsive column breakpoints:
//   < 600px  → 1 column  (phone / narrow canvas)
//   600–899px → 2 columns (intermediate window sizes)
//   ≥ 900px  → 3 columns (wide canvas / desktop)
const BREAKPOINT_2COL = 600
const BREAKPOINT_3COL = 900

// nativeID used by the web-only margin scroll-forwarding effect to locate the
// grid's scroll container in the DOM.
const CONTENT_LIBRARY_GRID_ID = "sk-content-library-grid"

interface ContentLibraryGridProps {
  items: ContentLibraryItem[]
  isLoading?: boolean
  /** Called when the user taps a non-locked item */
  onSelectItem?: (item: ContentLibraryItem) => void
  /** Called when the user taps a subscription-only locked item */
  onSelectLockedItem?: (item: ContentLibraryItem) => void
  /** When true, all item taps are suppressed (e.g. for admin/SME roles that can browse but not select) */
  selectionDisabled?: boolean
  style?: ViewStyle
  /** Extra bottom padding so cards don't hide under the control bar */
  contentPaddingBottom?: number
}

export function ContentLibraryGrid({
  items,
  isLoading,
  onSelectItem,
  onSelectLockedItem,
  selectionDisabled = false,
  style,
  contentPaddingBottom = 140,
}: ContentLibraryGridProps) {
  const { themed } = useAppTheme()

  // Measure the FlatList's actual rendered width so cards align precisely with
  // the sort bar elements regardless of viewport/container constraints on web.
  const [listWidth, setListWidth] = useState(0)
  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    setListWidth(e.nativeEvent.layout.width)
  }, [])

  // On web the grid renders inside a centered, max-width canvas, leaving empty
  // margins on either side for wider viewports. Mirror SidekickChat's treatment:
  // widen the grid's scroll container so its right edge (and the scrollbar) sits
  // at the window edge, making the right margin part of the native scroll area.
  // The left margin can't be reached by the same trick, so wheel events there are
  // forwarded to the scroll container. Cards keep their canvas-relative size and
  // position because width is measured on the (un-widened) wrapper, not here.
  useLayoutEffect(() => {
    if (Platform.OS !== "web") return

    const gridEl = document.getElementById(CONTENT_LIBRARY_GRID_ID)
    if (!gridEl) return

    // Clear overflow clipping up the ancestor chain so the widened scroll
    // container isn't clipped back to the centered canvas bounds.
    const layoutWrapper = document.getElementById("sk-layout-wrapper")
    const overriddenEls: HTMLElement[] = [gridEl]
    gridEl.style.overflow = "visible"
    let ancestor = gridEl.parentElement
    while (ancestor && ancestor !== layoutWrapper?.parentElement) {
      ancestor.style.overflow = "visible"
      overriddenEls.push(ancestor)
      ancestor = ancestor.parentElement
    }

    const isScrollable = (el: HTMLElement) => {
      const style = window.getComputedStyle(el)
      return style.overflowY === "auto" || style.overflowY === "scroll"
    }

    const findScrollContainer = (el: HTMLElement): HTMLElement | null => {
      if (isScrollable(el)) return el
      for (const child of Array.from(el.children)) {
        const found = findScrollContainer(child as HTMLElement)
        if (found) return found
      }
      return null
    }

    const scrollContainer = findScrollContainer(gridEl)

    let widthApplied = false
    if (scrollContainer) {
      const rect = scrollContainer.getBoundingClientRect()
      const rightMargin = Math.max(0, Math.floor(window.innerWidth - rect.right))
      if (rightMargin > 0) {
        scrollContainer.style.width = `calc(100% + ${rightMargin}px)`
        scrollContainer.style.overflowX = "hidden"
        widthApplied = true
      }
    }

    const onWheel = (e: WheelEvent) => {
      if (!scrollContainer) return
      const rect = scrollContainer.getBoundingClientRect()
      const inVerticalBounds = e.clientY >= rect.top && e.clientY <= rect.bottom
      // The right margin is now inside the scroll container, so only the left
      // margin (clientX < rect.left) needs forwarding.
      const inMargin = e.clientX < rect.left || e.clientX > rect.right
      if (inMargin && inVerticalBounds) {
        scrollContainer.scrollTop += e.deltaY
        e.preventDefault()
      }
    }

    window.addEventListener("wheel", onWheel, { passive: false })

    return () => {
      window.removeEventListener("wheel", onWheel)
      overriddenEls.forEach((el) => {
        el.style.overflow = ""
      })
      if (scrollContainer && widthApplied) {
        scrollContainer.style.width = ""
        scrollContainer.style.overflowX = ""
      }
    }
  })

  const numColumns = listWidth >= BREAKPOINT_3COL ? 3 : listWidth >= BREAKPOINT_2COL ? 2 : 1

  const availableWidth = listWidth - GRID_PADDING_LEFT - GRID_PADDING_RIGHT
  const cardWidth = Math.max(
    0,
    numColumns > 1 ? (availableWidth - CARD_GAP * (numColumns - 1)) / numColumns : availableWidth,
  )

  const renderItem = useCallback(
    ({ item }: { item: ContentLibraryItem }) => (
      <ContentLibraryCard
        item={item}
        cardWidth={cardWidth}
        onSelectItem={onSelectItem}
        onSelectLockedItem={onSelectLockedItem}
        selectionDisabled={selectionDisabled}
      />
    ),
    [cardWidth, onSelectItem, onSelectLockedItem, selectionDisabled],
  )

  const keyExtractor = useCallback((item: ContentLibraryItem) => item.id, [])

  if (isLoading) {
    return (
      <View style={[styles.loadingContainer, style]} onLayout={handleLayout}>
        <ActivityIndicator size="large" />
      </View>
    )
  }

  // Don't render items until the list width is measured — a zero cardWidth causes
  // ThumbnailCard to pass size={0} to MaterialIcons, which renders as fontSize:0
  // and crashes Android's text layout engine when letter spacing is present.
  if (listWidth === 0) {
    return <View style={[styles.loadingContainer, style]} onLayout={handleLayout} />
  }

  return (
    // Wrapper carries a nativeID so the web-only scroll-container effect can
    // locate the grid in the DOM. Width is measured here (not on the FlatList)
    // so widening the inner scroll container doesn't affect card sizing.
    <View
      nativeID={CONTENT_LIBRARY_GRID_ID}
      style={[styles.gridWrapper, style]}
      onLayout={handleLayout}
    >
      <FlatList
        // key must change when numColumns changes so React Native remounts the list
        key={`grid-${numColumns}`}
        data={items}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        numColumns={numColumns}
        columnWrapperStyle={numColumns > 1 ? { gap: CARD_GAP } : undefined}
        style={styles.gridWrapper}
        contentContainerStyle={[themed($contentContainer), { paddingBottom: contentPaddingBottom }]}
        showsVerticalScrollIndicator={true}
        ItemSeparatorComponent={() => (
          <View style={{ height: numColumns > 1 ? ROW_GAP_MULTI : CARD_GAP }} />
        )}
      />
    </View>
  )
}

interface ContentLibraryCardProps {
  item: ContentLibraryItem
  cardWidth: number
  onSelectItem?: (item: ContentLibraryItem) => void
  onSelectLockedItem?: (item: ContentLibraryItem) => void
  selectionDisabled?: boolean
}

const MEDIA_TYPE_LABEL: Record<string, string> = {
  video: "Video",
  pdf: "PDF",
  audio: "Audio",
  image: "Image",
  url: "Website",
  text: "Text",
  youtube: "YouTube",
}

const MEDIA_TYPE_ICON: Record<string, React.ComponentProps<typeof MaterialIcons>["name"]> = {
  video: "videocam",
  audio: "headset",
  pdf: "picture-as-pdf",
  image: "image",
  url: "language",
  text: "article",
  youtube: "videocam",
}

function formatPublishedDate(dateStr: string): string {
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ""
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
}

const PUBLISHING_STATUS_CONFIG: Record<
  string,
  { label: string; dotColor: string; backgroundColor: string; borderColor: string }
> = {
  published: {
    label: "Published",
    dotColor: "#16A34A",
    backgroundColor: "rgba(22, 163, 74, 0.12)",
    borderColor: "#16A34A",
  },
  testing: {
    label: "Testing",
    dotColor: "#2563EB",
    backgroundColor: "rgba(37, 99, 235, 0.12)",
    borderColor: "#2563EB",
  },
  pending: {
    label: "Pending",
    dotColor: "#CA8A04",
    backgroundColor: "rgba(234, 179, 8, 0.12)",
    borderColor: "#CA8A04",
  },
}

function normalizePublishingStatus(status: string | null | undefined): string | null {
  if (!status) return null
  const lower = status.toLowerCase()
  if (lower === "pending_approval") return "pending"
  return lower
}

const ContentLibraryCard = React.memo(function ContentLibraryCard({
  item,
  cardWidth,
  onSelectItem,
  onSelectLockedItem,
  selectionDisabled,
}: ContentLibraryCardProps) {
  const { themed } = useAppTheme()
  const currentRole = useCurrentRole()
  const isTestingMember = currentRole === "testing_member"
  const thumbnailWidth = cardWidth - 2 // add padding to prevent border radius cropping WEB-75
  const cardHeight = Math.round(thumbnailWidth / THUMBNAIL_ASPECT_RATIO)

  const handlePress = useCallback(() => {
    if (selectionDisabled) return
    if (item.subscriptionOnly) {
      onSelectLockedItem?.(item)
    } else {
      onSelectItem?.(item)
    }
  }, [selectionDisabled, item, onSelectItem, onSelectLockedItem])

  const avatarSource = useMemo(() => {
    const key = item.sidekickAvatarId?.padStart(2, "0") ?? "00"
    return avatarImageMap[key] ?? avatarImageMap["00"]
  }, [item.sidekickAvatarId])

  const statusConfig = useMemo(() => {
    if (!isTestingMember) return null
    const normalized = normalizePublishingStatus(item.publishingStatus)
    return normalized ? (PUBLISHING_STATUS_CONFIG[normalized] ?? null) : null
  }, [isTestingMember, item.publishingStatus])

  return (
    <TouchableOpacity
      onPress={handlePress}
      activeOpacity={selectionDisabled ? 1 : 0.85}
      disabled={selectionDisabled}
      style={[themed($card), { width: cardWidth }]}
      accessibilityRole="button"
      accessibilityLabel={item.title}
      accessibilityState={{ disabled: selectionDisabled }}
    >
      {/* Thumbnail */}
      <View
        style={{ width: thumbnailWidth, height: cardHeight, borderRadius: 12, overflow: "hidden" }}
      >
        <ThumbnailCard item={item} width={thumbnailWidth} />

        {/* Lock overlay for subscription-only items */}
        {item.subscriptionOnly && (
          <View style={[StyleSheet.absoluteFill, styles.lockOverlay]}>
            <View style={styles.lockBadge}>
              <MaterialIcons name="lock" size={20} color="#fff" />
            </View>
          </View>
        )}
      </View>

      {/* Title + sidekick info row */}
      <View style={themed($cardFooter)}>
        {/* Avatar stacked above sidekick name */}
        <View style={styles.avatarColumn}>
          <Image source={avatarSource} style={themed($avatarImage)} />
          <Text style={themed($sidekickName)} numberOfLines={2}>
            {item.sidekickName}
          </Text>
        </View>
        <View style={styles.cardTextContainer}>
          <Text style={themed($cardTitle)} numberOfLines={2}>
            {item.title}
          </Text>
          {statusConfig && (
            <View
              style={[
                styles.statusPill,
                {
                  backgroundColor: statusConfig.backgroundColor,
                  borderColor: statusConfig.borderColor,
                },
              ]}
            >
              <View style={[styles.statusDot, { backgroundColor: statusConfig.dotColor }]} />
              <Text style={[themed($statusPillText), { color: statusConfig.borderColor }]}>
                {statusConfig.label}
              </Text>
            </View>
          )}
          {(item.mediaType || item.lastPublishedAt) && (
            <View style={styles.detailRow}>
              {item.mediaType && (
                <>
                  <MaterialIcons
                    name={MEDIA_TYPE_ICON[item.mediaType] ?? "insert-drive-file"}
                    size={13}
                    style={themed($detailIcon)}
                  />
                  <Text style={themed($detailText)}>
                    {MEDIA_TYPE_LABEL[item.mediaType] ?? item.mediaType}
                  </Text>
                </>
              )}
              {item.mediaType && item.lastPublishedAt && (
                <Text style={themed($detailText)}>{" | "}</Text>
              )}
              {item.lastPublishedAt && (
                <Text style={themed($detailText)}>
                  {"Published " + formatPublishedDate(item.lastPublishedAt)}
                </Text>
              )}
            </View>
          )}
        </View>
      </View>
    </TouchableOpacity>
  )
})

const styles = StyleSheet.create({
  gridWrapper: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  lockOverlay: {
    backgroundColor: "rgba(0,0,0,0.35)",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: 10,
    borderRadius: 12,
  },
  lockBadge: {
    backgroundColor: "rgba(0,0,0,0.6)",
    borderRadius: 16,
    padding: 6,
  },
  avatarColumn: {
    alignItems: "center",
    width: 52,
    flexShrink: 0,
  },
  cardTextContainer: {
    flex: 1,
    paddingLeft: 10,
    justifyContent: "center",
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: 3,
    gap: 3,
  },
  statusPill: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderRadius: 20,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginTop: 4,
    gap: 5,
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
})

const $contentContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  paddingTop: spacing.sm,
  paddingLeft: GRID_PADDING_LEFT,
  paddingRight: GRID_PADDING_RIGHT,
  gap: 0,
})

const $card: ThemedStyle<ViewStyle> = ({ colors }) => ({
  borderRadius: 12,
  backgroundColor: colors.background,
  overflow: "hidden",
  marginBottom: 0,
  borderWidth: 1,
  borderColor: colors.cardBorder,
})

const $cardFooter: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexDirection: "row",
  alignItems: "center",
  padding: spacing.sm,
})

const $avatarImage: ThemedStyle<ImageStyle> = ({ colors }) => ({
  width: 40,
  height: 40,
  borderRadius: 20,
  backgroundColor: colors.activeAvatarBackground ?? "#888",
})

const $cardTitle: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 14,
  fontFamily: typography.primary.medium,
  color: colors.text,
  lineHeight: 20,
})

const $sidekickName: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 11,
  fontFamily: typography.primary.normal,
  color: colors.text,
  textAlign: "center",
  marginTop: 4,
})

const $detailText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 11,
  fontFamily: typography.primary.normal,
  color: colors.textDim,
})

const $detailIcon: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.textDim,
})

const $statusPillText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  fontSize: 11,
  fontFamily: typography.primary.medium,
  color: colors.text,
})
